import React, { useRef } from 'react';
import styled, { css } from 'styled-components';

import { GqlFilteredSpecials } from 'types/graphql';

import { chunk } from 'src/utils/array';
import { VisuallyHidden } from 'shared/components/visually-hidden';
import ChevronIcon from 'src/assets/chevron-icon';
import { SpecialCard } from 'src/components/special-card';
import { useCarousel } from './use-carousel';

type ProductSpecialsCarouselV2Props = {
  specials: GqlFilteredSpecials['specials'];
};

export const ProductSpecialsCarouselV2 = ({ specials }: ProductSpecialsCarouselV2Props): JSX.Element | null => {
  const viewportRef = useRef<HTMLDivElement>(null);
  const { currentIndex, handlePrevious, handleNext, handleScroll } = useCarousel({
    viewportRef,
  });

  if (!specials?.length) {
    return null;
  }

  const specialGroups = chunk(specials, 3);

  return (
    <>
      <Heading>Special Offers ( {specials.length} )</Heading>

      <Viewport ref={viewportRef}>
        {specialGroups.map((group, index) => (
          <Group data-index={index} key={group.map((s) => s?._id).join('-')}>
            {group.map((special) => special && <SpecialCard key={special._id} special={special} />)}
          </Group>
        ))}
      </Viewport>

      {specialGroups.length > 1 && (
        <Controls>
          <PrevNextButton onClick={handlePrevious} disabled={currentIndex === 0}>
            <VisuallyHidden>Previous</VisuallyHidden>
            <PrevArrow />
          </PrevNextButton>

          {specialGroups.map((group, index) => (
            <Dot
              key={group.map((s) => s?._id).join('-')}
              onClick={() => handleScroll(index)}
              disabled={currentIndex === index}
            >
              <VisuallyHidden>Go to group {index}</VisuallyHidden>
            </Dot>
          ))}

          <PrevNextButton onClick={handleNext} disabled={currentIndex === specialGroups.length - 1}>
            <VisuallyHidden>Next</VisuallyHidden>
            <NextArrow />
          </PrevNextButton>
        </Controls>
      )}
    </>
  );
};

const Heading = styled.h2`
  color: #242526;
  font-size: 16px;
  font-weight: bold;
  line-height: ${20 / 16};
  margin-bottom: 16px;
`;

const Viewport = styled.div`
  display: flex;
  gap: 8px;
  overflow-x: auto;
  scroll-behavior: smooth;
  scroll-snap-type: x mandatory;
  -ms-overflow-style: none;
  scrollbar-width: none;
  width: 100%;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const Controls = styled.div`
  align-items: center;
  display: flex;
  gap: 8px;
  justify-content: center;
  margin-top: 16px;
`;

const buttonReset = css`
  appearance: none;
  background: none;
  border: none;
  padding: 0;

  &:not(:disabled) {
    cursor: pointer;
  }
`;

const Group = styled.div`
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  scroll-snap-align: center;
  gap: 8px;
  width: 100%;
`;

const PrevNextButton = styled.button`
  ${buttonReset}
  align-items: center;
  color: #2c3236;
  display: flex;
  height: 16px;
  justify-content: center;
  width: 16px;

  ${({ theme }) => theme.breakpoints.down('sm')} {
    display: none;
  }

  &[disabled] {
    color: #e3e7e9;
  }
`;

const prevNextArrowStyles = css`
  height: 7px;
  width: 11px;
`;

const PrevArrow = styled(ChevronIcon)`
  ${prevNextArrowStyles}
  transform: rotate(90deg);
`;
const NextArrow = styled(ChevronIcon)`
  ${prevNextArrowStyles}
  transform: rotate(-90deg);
`;

const Dot = styled.button`
  ${buttonReset}
  background: #e3e7e9;
  border-radius: 50%;
  height: 8px;
  position: relative;
  width: 8px;

  &[disabled] {
    background: #121516;
  }

  /* Increase click target */
  &::before {
    content: '';
    height: 16px;
    left: 50%;
    position: absolute;
    top: 50%;
    transform: translate(-50%, -50%);
    width: 16px;
  }
`;
